import { Text } from '@chakra-ui/react';
import VStackColumn from '@voly/ui-components/src/v2/VStackColumn.jsx';
import VStackRow from '@voly/ui-components/src/v2/VStackRow.jsx';
import { Fragment, ReactElement } from 'react';
import {
  deconstructConditionalFormula,
  deconstructFormula,
} from 'src/app/project/components/formula-builder/utils/deconstruct-formula';
import {
  EFormulaPieceType,
  TConditionalFormula,
  TFormulaPiece,
} from 'src/app/project/components/formula-builder/utils/formula-input.type';
import { translate } from 'src/app/project/features/translate/translate';
import { TWorkspaceCustomFields } from 'src/app/project/modules/custom-fields/custom-fields.model';
import CustomFieldTypeIcon from './CustomFieldTypeIcon';

const CONDITIONAL_PREFIX = 'if';
const FORMULA_LETTER = 'f';

const referencesHiddenField = (
  pieces: TFormulaPiece[],
  customFields: TWorkspaceCustomFields,
): boolean =>
  pieces.some(
    (piece) => piece.type === EFormulaPieceType.CUSTOM_FIELD && !customFields[piece.value]?.display,
  );

const joinOperatorRuns = (pieces: TFormulaPiece[]): TFormulaPiece[] =>
  pieces.reduce<TFormulaPiece[]>((runs, piece) => {
    const previous = runs[runs.length - 1];
    if (
      previous?.type === EFormulaPieceType.OPERATOR &&
      piece.type === EFormulaPieceType.OPERATOR
    ) {
      return [...runs.slice(0, -1), { ...previous, value: `${previous.value}${piece.value}` }];
    }
    return [...runs, piece];
  }, []);

const FormulaExpression = ({
  pieces,
  customFields,
}: {
  pieces: TFormulaPiece[];
  customFields: TWorkspaceCustomFields;
}): ReactElement => (
  <VStackRow gap={2.5} wrap="wrap">
    {joinOperatorRuns(pieces).map((piece, index) =>
      piece.type === EFormulaPieceType.CUSTOM_FIELD ? (
        <VStackRow
          key={index}
          gap={1}
          paddingX={1}
          paddingY={0.5}
          maxWidth={24}
          borderWidth="1px"
          borderColor="primary.muted"
          borderRadius="sm"
          bg="bg.panel"
          color="primary.solid"
        >
          <CustomFieldTypeIcon field={customFields[piece.value]} size="sm" />
          <Text truncate>{customFields[piece.value].label}</Text>
        </VStackRow>
      ) : (
        <Text key={index} fontWeight="semibold">
          {piece.value}
        </Text>
      ),
    )}
  </VStackRow>
);

interface FormulaShapeProps {
  customFields: TWorkspaceCustomFields;
}

const SimpleFormula = ({
  pieces,
  customFields,
}: FormulaShapeProps & { pieces: TFormulaPiece[] }): ReactElement => (
  <VStackRow gap={2}>
    <Text fontStyle="italic" fontWeight="semibold">
      {FORMULA_LETTER}
    </Text>
    <FormulaExpression pieces={pieces} customFields={customFields} />
  </VStackRow>
);

const ConditionalFormula = ({
  formula: { condition, truthy, falsey },
  customFields,
}: FormulaShapeProps & { formula: TConditionalFormula }): ReactElement => (
  <VStackColumn gap={1}>
    <Text>{translate('conditional_formula_condition_label')}</Text>
    <FormulaExpression pieces={condition} customFields={customFields} />
    <Text>{translate('conditional_formula_truthy_label')}</Text>
    <FormulaExpression pieces={truthy} customFields={customFields} />
    <Text>{translate('conditional_formula_falsy_label')}</Text>
    <FormulaExpression pieces={falsey} customFields={customFields} />
  </VStackColumn>
);

const allPieces = ({ condition, truthy, falsey }: TConditionalFormula): TFormulaPiece[] => [
  ...condition,
  ...truthy,
  ...falsey,
];

interface FormulaTooltipProps {
  formula: string;
  customFields: TWorkspaceCustomFields;
}

const FormulaTooltip = ({ formula, customFields }: FormulaTooltipProps): ReactElement => {
  const conditional = formula.startsWith(CONDITIONAL_PREFIX)
    ? deconstructConditionalFormula(formula)
    : null;
  const pieces = conditional ? allPieces(conditional) : deconstructFormula(formula);

  if (referencesHiddenField(pieces, customFields)) {
    return <Fragment>{translate('formula_result')}</Fragment>;
  }

  if (conditional) {
    return <ConditionalFormula formula={conditional} customFields={customFields} />;
  }

  return <SimpleFormula pieces={pieces} customFields={customFields} />;
};

export default FormulaTooltip;
